import { useCallback, useEffect, useRef, useState } from 'react'
import { CHAOS_THRESHOLD } from '@sh/engine'
import type { Policy, PublicView } from '@sh/shared'

/**
 * Something worth interrupting a phone for. These are derived by diffing two
 * consecutive views rather than being pushed by the server, because the server
 * deliberately sends state and not a log - and because a reconnecting phone
 * must not replay an execution it already watched.
 */
export type WinReason = NonNullable<PublicView['winReason']>

/** Exported so a story can build one without reaching for a non-distributive Omit. */
export type EventBody =
  | { kind: 'policy'; policy: Policy; chaos: boolean; liberal: number; fascist: number }
  | { kind: 'execution'; name: string; isYou: boolean }
  /**
   * The Veto Power coming into effect, which is the one thing that happens on
   * this board with nobody doing it. Every other scene announces a person's
   * move; this announces a rule that quietly changed under the whole table on
   * the fifth fascist policy, is listed nowhere on the physical board, and is
   * therefore the rule a table reliably forgets it has until somebody tries to
   * use it in the last round of the game.
   */
  | { kind: 'veto' }
  /**
   * The discard pile going back into the draw pile, which is the other thing
   * on this board that nobody does.
   *
   * It is announced because it is the moment every card counter at the table
   * is reset. Watching the tiles go by is the one legitimate read a liberal
   * has on a deck stacked eleven to six, and the instant the piles are merged
   * that read is worth nothing - but on the board it is two small numbers
   * quietly swapping, and a table absorbed in arguing about the last
   * government does not see it happen.
   */
  | { kind: 'shuffle'; /** How big the draw pile is now. */ deck: number }
  | { kind: 'presidency'; name: string; isYou: boolean; special: boolean }
  /**
   * A ballot that has resolved, and who stood on each side of it.
   *
   * Both outcomes, not only the rejections. The counts and the two lists have
   * always been public the instant a vote resolves - the board prints them -
   * but half of every game's ballots are the half that *passed*, and those
   * went by with nothing said at all: the phone simply moved on to the
   * legislative session and the table was left asking who had voted for this.
   * At a table the interesting fact is never 6-3, it is which three, so the
   * names are the body of the scene rather than a footnote to it.
   *
   * Names rather than ids, resolved while the roster is still to hand: a
   * player executed two rounds later is still in the view, but nothing
   * downstream of here should have to rely on that.
   */
  | {
      kind: 'election'
      passed: boolean
      president: string
      chancellor: string
      ja: string[]
      nein: string[]
      /**
       * How far the Election Tracker got on this ballot - three on the
       * rejection that tips the country into chaos, even though the board has
       * already cleared it back to nought by then. Only drawn on a rejection.
       */
      tracker: number
    }
  | {
      kind: 'victory'
      winner: 'liberal' | 'fascist'
      reason: WinReason
      /** Null on a table screen, which belongs to nobody. */
      youWon: boolean | null
    }

/** Numbered so the queue can drop exactly the one that is playing. */
export type GameEvent = EventBody & { id: number }

/**
 * How long each kind holds the screen. This is solid time now: the backdrop
 * stays up between queued scenes and only fades once the queue is empty, so
 * none of it is spent on a fade.
 *
 * Set at roughly twice what it was. A scene is being read across a table by
 * ten people who are all looking somewhere else when it starts, and at the old
 * timings the room's answer to "what just happened?" was regularly "no idea" -
 * the card had flipped and gone before anybody's eyes were on the phone. The
 * entrance choreography in the stylesheet is unchanged; all of the extra time
 * lands at the end of a scene, where it is reading time. Nothing is a dead
 * wait: the scene draws its own countdown (see Cinematic), and a tap skips.
 */
export const EVENT_MS: Record<GameEvent['kind'], number> = {
  policy: 5300,
  execution: 5300,
  presidency: 3700,
  // A rule to read rather than a name to recognise, and one nobody was
  // expecting, so it gets the long end of the range.
  veto: 5600,
  // Two columns of names to read across a table, where the old one was two
  // numbers.
  election: 5200,
  // One fact, no name in it, and nothing to decide off the back of it. The
  // short end of the range on purpose: it lands mid-session, usually right
  // behind the policy that emptied the deck, and the table is still looking at
  // the tracks.
  shuffle: 3600,
  // The one that ends the evening, so it is given room to land.
  victory: 8000,
}

/**
 * The scenes one view update brings. Exported for /learn, which replays its
 * scripted moves through the same scenes a table would see.
 */
export function diff(previous: PublicView, next: PublicView, youId: string | null): EventBody[] {
  const out: EventBody[] = []
  const name = (id: string) => next.players.find((p) => p.id === id)?.name ?? 'someone'
  // Dealing a new game is not a sequence of things that happened at the table.
  if (previous.phase === 'lobby' || next.phase === 'lobby') return out

  /**
   * The populace acted on its own this update - which only ever happens
   * because the Election Tracker just reached three.
   */
  const chaos = next.announcements.some((a) => a.kind === 'chaos_policy')

  // The ballot, before the policy a third rejection would enact.
  const vote = next.lastVote
  const before = previous.lastVote
  const newVote =
    vote &&
    (!before ||
      before.presidentId !== vote.presidentId ||
      before.chancellorId !== vote.chancellorId ||
      before.ja.length !== vote.ja.length ||
      before.nein.length !== vote.nein.length ||
      before.passed !== vote.passed)
  if (newVote && vote) {
    out.push({
      kind: 'election',
      passed: vote.passed,
      president: name(vote.presidentId),
      chancellor: name(vote.chancellorId),
      ja: vote.ja.map(name),
      nein: vote.nein.map(name),
      // Enacting a policy clears the tracker, and the third rejection enacts
      // one, so by the time this view lands the count that caused the chaos
      // has already been wiped off it. Reading `electionTracker` straight
      // would announce the rejection that tips the country over as "0 of 3".
      // The scene is about the ballot that just failed, not about the board a
      // moment after it, so the chaos itself stands in for the third dot.
      tracker: chaos ? CHAOS_THRESHOLD : next.electionTracker,
    })
  }

  if (next.liberalPolicies > previous.liberalPolicies) {
    out.push({
      kind: 'policy',
      policy: 'liberal',
      chaos,
      liberal: next.liberalPolicies,
      fascist: next.fascistPolicies,
    })
  }
  if (next.fascistPolicies > previous.fascistPolicies) {
    out.push({
      kind: 'policy',
      policy: 'fascist',
      chaos,
      liberal: next.liberalPolicies,
      fascist: next.fascistPolicies,
    })
  }

  // After the policy that did it, because that is the order the table saw it
  // in: the card goes down, and then the rule it brought with it lands.
  if (!previous.vetoUnlocked && next.vetoUnlocked) {
    out.push({ kind: 'veto' })
  }

  /*
   * And after the veto, because the deck is topped up at the very end of the
   * legislative session - after the policy is down and after the rule it
   * brought with it - and before whatever power the President is about to use.
   *
   * Both halves of the test are needed. The announcement alone repeats: it is
   * replaced wholesale only when the next vote resolves, so it sits in the
   * view for the rest of the round and every unrelated update in between would
   * replay the scene. The deck growing alone lies: dealing a fresh game also
   * grows it, and the host can start one straight from the result screen
   * without passing through the lobby this function already ignores.
   */
  const reshuffled =
    next.deckSize > previous.deckSize &&
    next.announcements.some((a) => a.kind === 'deck_reshuffled')
  if (reshuffled) {
    out.push({ kind: 'shuffle', deck: next.deckSize })
  }

  for (const player of next.players) {
    const was = previous.players.find((p) => p.id === player.id)
    if (was?.alive && !player.alive) {
      out.push({ kind: 'execution', name: player.name, isYou: player.id === youId })
    }
  }

  // Not during the reveal: the first placard of a game would land on top of a
  // role card somebody is in the middle of reading.
  const president = next.presidentId
  if (president && president !== previous.presidentId && next.phase !== 'role_reveal') {
    out.push({
      kind: 'presidency',
      name: name(president),
      isYou: president === youId,
      special: next.announcements.some((a) => a.kind === 'special_election'),
    })
  }

  // Last, so it lands after the policy or the execution that caused it. An
  // abandoned game is not a win and gets nothing.
  if (next.phase === 'game_over' && previous.phase !== 'game_over' && next.winner && next.winReason) {
    // Roles become public exactly when the game ends, so this is the first
    // moment the client can honestly say whether you were on the winning side.
    const role = youId ? next.roles?.[youId] : null
    out.push({
      kind: 'victory',
      winner: next.winner,
      reason: next.winReason,
      youWon: role ? (role === 'liberal') === (next.winner === 'liberal') : null,
    })
  }

  return out
}

/**
 * The current cinematic, if any, and the queue behind it. Several things can
 * land in one view update - a rejected government that enacts a chaos policy
 * and hands the placard on - so they are played one after another rather than
 * fighting over the screen.
 */
export interface GameEventOptions {
  /** Restricts what this screen announces; table screens take the win only. */
  only?: readonly GameEvent['kind'][]
  /**
   * Kinds that wait for a tap instead of timing out. Only worth it for the
   * scene that ends the game, and only on a phone: a table screen has to clear
   * itself, because nobody walks over to a television to dismiss it.
   */
  holdForTap?: readonly GameEvent['kind'][]
}

export function useGameEvents(
  view: PublicView | null,
  youId: string | null,
  /** Pass a constant, not a fresh object each render. */
  options: GameEventOptions = {},
): { event: GameEvent | null; skip: () => void; awaitingTap: boolean } {
  const { only, holdForTap } = options
  const [queue, setQueue] = useState<GameEvent[]>([])
  const previous = useRef<PublicView | null>(null)
  const nextId = useRef(1)

  useEffect(() => {
    if (!view) return
    const last = previous.current
    previous.current = view
    // The first view of a session is the state as it already stands: a phone
    // that reconnects mid-game must not replay what it missed.
    if (!last) return
    const found = diff(last, view, youId).filter((e) => !only || only.includes(e.kind))
    if (found.length === 0) return
    setQueue((q) => [...q, ...found.map((e): GameEvent => ({ ...e, id: nextId.current++ }))])
  }, [view, youId, only])

  const current = queue[0] ?? null
  const awaitingTap = current !== null && Boolean(holdForTap?.includes(current.kind))

  useEffect(() => {
    if (!current || awaitingTap) return
    const timer = window.setTimeout(
      () => setQueue((q) => q.filter((e) => e.id !== current.id)),
      EVENT_MS[current.kind],
    )
    return () => window.clearTimeout(timer)
  }, [current, awaitingTap])

  // Skipping drops this one and lets whatever is queued behind it start now,
  // rather than waiting out a timer nobody is watching. For a held scene this
  // is the only way out.
  const skip = useCallback(() => setQueue((q) => q.slice(1)), [])

  return { event: current, skip, awaitingTap }
}

/**
 * True for `ms` after `value` changes, so a board element can animate the thing
 * that just happened without the server having to tell it twice.
 */
export function useFlash<T>(value: T, ms = 1400): boolean {
  const [on, setOn] = useState(false)
  const previous = useRef<T | null>(null)
  const first = useRef(true)

  useEffect(() => {
    if (first.current) {
      first.current = false
      previous.current = value
      return
    }
    if (previous.current === value) return
    previous.current = value
    setOn(true)
    const timer = window.setTimeout(() => setOn(false), ms)
    return () => window.clearTimeout(timer)
  }, [value, ms])

  return on
}
